import { 
  Directive, 
  ElementRef, 
  EventEmitter, 
  forwardRef, 
  HostListener, 
  NgZone, 
  OnDestroy, 
  Output,
  AfterViewInit
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import intlTelInput from 'intl-tel-input';
import { Subject } from 'rxjs';

export interface PhoneData {
  nationalNumber: string;
  dialCode: string;
  fullNumber: string;
  isValid: boolean;
}

@Directive({
  selector: '[appIntlTelInput]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IntlTelInputDirective),
      multi: true
    }
  ]
})
export class IntlTelInputDirective implements ControlValueAccessor, OnDestroy, AfterViewInit {
  private iti: any;
  private onTouched: any = () => {};
  private onChange: any = () => {};
  private destroy$ = new Subject<void>();
  
  @Output() phoneChange = new EventEmitter<PhoneData>();

  @HostListener('blur')
  onBlur() {
    this.onTouched();
  }

  constructor(
    private elementRef: ElementRef,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit() {
    if (!this.elementRef.nativeElement) return;

    // Store directive reference in the native element for external access
    this.elementRef.nativeElement._directive = this;

    // Initialize intl-tel-input
    this.ngZone.runOutsideAngular(() => {
      this.iti = intlTelInput(this.elementRef.nativeElement, {
        separateDialCode: true,
        formatOnDisplay: true,
        utilsScript: `https://cdn.jsdelivr.net/npm/intl-tel-input@${intlTelInput.version}/build/js/utils.js`,
      });

      this.elementRef.nativeElement.addEventListener('input', () => {
        this.handleInputChange();
      });

      this.elementRef.nativeElement.addEventListener('countrychange', () => {
        //this.handleInputChange();
      });
    });
  }

  writeValue(value: string): void {
    if (this.iti) {
      this.ngZone.runOutsideAngular(() => {
        this.iti.setNumber(value);
        // Trigger input change to update the form values
        setTimeout(() => {
          this.handleInputChange();
        }, 0);
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  handleInputChange(): void {
    this.ngZone.run(() => {
      if (!this.iti) return;

      try {
        const phoneNumber = this.iti.getNumber();
        const countryData = this.iti.getSelectedCountryData();
        
        if (countryData && countryData.dialCode) {
          const dialCode = countryData.dialCode;
          let nationalNumber = '';
          let isValid = false;
          
          if (phoneNumber) {
            nationalNumber = phoneNumber.replace(`+${dialCode}`, '').trim();
            this.onChange(phoneNumber);
            isValid = this.iti.isValidNumber();
          }
          
          this.phoneChange.emit({
            nationalNumber,
            dialCode,
            fullNumber: phoneNumber,
            isValid
          });
        }
      } catch (error) {
        console.error('Error getting data from phone:', error);
      }
    });
  }

  /**
   * Gets the current phone number in the specified format
   * @returns the formatted phone number
   */
  getNumber(): string | undefined {
    return this.iti?.getNumber();
  }

  /**
   * Gets the selected country data
   * @returns the country data object
   */
  getSelectedCountryData(): any {
    return this.iti?.getSelectedCountryData();
  }

  /**
   * Checks if the current number is valid
   * @returns true if valid, false otherwise
   */
  isValidNumber(): boolean {
    return this.iti?.isValidNumber() || false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.iti) {
      this.ngZone.runOutsideAngular(() => {
        this.iti.destroy();
      });
    }

    // Remove the reference from the native element
    if (this.elementRef.nativeElement) {
      delete this.elementRef.nativeElement._directive;
    }
  }
} 